import {
  diffMetrics, diffReadable, evidenceWord, TIER_WORD,
  type Diferenciais, type Tier,
} from '@ads/shared';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import * as Ic from '../components/icons';
import { useToolReading } from '../lib/queries';
import { Pill, type DiffFiles } from './DiffParts';
import { Card, ReadingStatus } from './GapsDashboard';

/* ─────────────────────────────────────────────────────────────────────
   Diferenciais · Análise consolidada. As somas saem da tabela na hora;
   a força real, a consistência, a qualidade das evidências e o texto
   são do agente, que relê notas e anexos alguns segundos depois de cada
   mudança. Enquanto ele lê, o que está na tela fica esmaecido.
   ───────────────────────────────────────────────────────────────────── */

/** "Força" é feminino: Forte, Moderada, Fraca. */
const FEM: Record<Tier, string> = { forte: 'Forte', moderado: 'Moderada', fraco: 'Fraca' };
const n1 = (x: number) => x.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ext = (name: string) => (/\.([a-z0-9]{1,5})$/i.exec(name)?.[1] ?? '—').toUpperCase();

export function DiferenciaisAnalise({ content, files, onEdit, onEvidence }: {
  content: Diferenciais; files: DiffFiles; onEdit: () => void; onEvidence: (id: string, name: string) => void;
}) {
  const q = useToolReading('diferenciais');
  const r = q.data;
  const reading = r?.reading ?? null;
  const updating = !!r && (r.pending || r.stale);
  const readable = useMemo(() => diffReadable(content), [content]);
  const m = useMemo(() => diffMetrics({ items: readable }, reading), [readable, reading]);
  const st = updating ? ' is-stale' : '';

  if (!readable.length) {
    return (
      <section className="card d-empty-card">
        <Ic.Sparkle />
        <h3>A análise começa pela trinca</h3>
        <p>Dê nome e as três notas a pelo menos um diferencial. A IA lê as notas e as evidências e monta a análise consolidada.</p>
        <button type="button" className="btn-primary" onClick={onEdit}>Ir para o preenchimento<Ic.ArrowRight /></button>
      </section>
    );
  }

  const cons = reading?.consistencia;
  const consAvg = cons ? Math.round((cons.realidade + cons.distincao + cons.dificuldade) / 3) : null;
  const ia = m.total.ia;
  const iaRows = m.rows.filter(x => x.ia);
  const counts = { forte: 0, moderado: 0, fraco: 0 } as Record<Tier, number>;
  for (const x of iaRows) counts[x.ia!.tier]++;
  const avg = iaRows.length ? iaRows.reduce((s, x) => s + x.ia!.sum, 0) / iaRows.length : null;
  const unread = readable.reduce((s, d) => s + (files.get(d.id ?? '') ?? []).filter(f => f.reading === 'pending' || f.reading === 'reading').length, 0);

  return (
    <div className="gd dd">
      <div className="notice dd-intro"><Ic.Sparkle />
        <p><b>Leitura da IA.</b> A análise considera as suas notas e as evidências anexadas a cada diferencial, cruzando consistência, qualidade das provas e relevância frente ao mercado.</p>
        <ReadingStatus updating={updating} basic={r?.basic ?? false} at={r?.updatedAt ?? null} />
      </div>
      {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}

      {/* ── 1 · os três números ── */}
      <div className="gd-row gd-three">
        <Card icon={<Ic.Target />} title="Força real dos diferenciais" sub="A soma da trinca que as evidências sustentam">
          {ia ? (
            <div className={`dd-kpi${st}`}>
              <div className="dd-kv"><b className="gd-big">{ia.sum}<small>/{m.max}</small></b><span className={`tbadge t-${ia.tier}`}>{FEM[ia.tier]}</span></div>
              <div className="dd-bar"><i className={`t-${ia.tier}`} style={{ '--v': `${ia.pct}%` } as CSSProperties} /><span>{ia.pct}%</span></div>
              <p className="dd-cmp">Sua avaliação: <b>{m.total.user.sum}/{m.max}</b> · {FEM[m.total.user.tier]}
                {ia.sum !== m.total.user.sum && <em className={ia.sum > m.total.user.sum ? 'up' : 'down'}>{ia.sum > m.total.user.sum ? '+' : ''}{ia.sum - m.total.user.sum} pela IA</em>}</p>
            </div>
          ) : <Waiting updating={updating} />}
        </Card>
        <Card icon={<Ic.Bars />} title="Consistência" sub="O quanto as suas notas coincidem com as evidências">
          {cons ? (
            <div className={`dd-kpi${st}`}>
              <div className="dd-kv"><b className="gd-big">{consAvg}<small>/100</small></b></div>
              <ul className="gd-bars dd-cons">
                {([['Realidade', cons.realidade], ['Distinção', cons.distincao], ['Dificuldade de cópia', cons.dificuldade]] as const).map(([l, v]) => (
                  <li key={l}><span className="nm">{l}</span><span className="tr"><i style={{ '--v': `${v}%` } as CSSProperties} /></span><span className="v"><b>{v}</b></span></li>
                ))}
              </ul>
            </div>
          ) : <Waiting updating={updating} />}
        </Card>
        <Card icon={<Ic.Doc />} title="Qualidade das evidências" sub="Cobertura e solidez das provas anexadas">
          {reading ? (
            <div className={`dd-kpi${st}`}>
              <div className="dd-kv"><b className="gd-big">{reading.evidencias.score}<small>/100</small></b><span className={`tbadge q-${evidenceWord(reading.evidencias.score).toLowerCase().replace('ó', 'o')}`}>{evidenceWord(reading.evidencias.score)}</span></div>
              {reading.evidencias.leitura && <p className="dd-text">{reading.evidencias.leitura}</p>}
            </div>
          ) : <Waiting updating={updating} />}
        </Card>
      </div>

      <div className="notice dd-banner"><Ic.Doc />
        <p><b>Evidências analisadas: {m.withFiles} de {m.n} {m.n === 1 ? 'diferencial' : 'diferenciais'} com anexos.</b>
          {' '}{unread ? `${unread} ${unread === 1 ? 'documento ainda está' : 'documentos ainda estão'} sendo lido${unread === 1 ? '' : 's'} pelo ADS.` : 'Os documentos de cada diferencial foram lidos pelo ADS e considerados junto com a sua avaliação.'}</p>
      </div>

      {/* ── 2 · diferencial por diferencial ── */}
      <Card icon={<Ic.Grid4 />} title="Leitura por diferencial" sub="Sua avaliação, as evidências e a força real estimada pela IA">
        <div className="tablewrap nomask">
          <table className="dense ddtable">
            <thead><tr><th>#</th><th>Diferencial</th><th className="c">Sua avaliação</th><th>Evidências</th><th className="c">Força real (IA)</th><th className="c">Status</th><th>Leitura da IA</th></tr></thead>
            <tbody>
              {m.rows.map((x, i) => {
                const fs = files.get(x.id) ?? [];
                return (
                  <tr key={x.id}>
                    <td className="mono">{i + 1}</td>
                    <td className="dd-name">{x.label}</td>
                    <td className="c" data-label="Sua avaliação"><Pill value={x.user.sum} tier={x.user.tier} /></td>
                    <td className="dd-ev">
                      {fs.length ? (
                        <button type="button" className="dd-files" onClick={() => onEvidence(x.id, x.label)} title={fs.map(f => f.filename).join('\n')}>
                          {fs.slice(0, 2).map(f => <span key={f.id} className="dd-file"><b>{ext(f.filename)}</b><span>{f.catalog?.title ?? f.filename}</span></span>)}
                          {fs.length > 2 && <em>+{fs.length - 2}</em>}
                        </button>
                      ) : (
                        <button type="button" className="evid g-evid none" onClick={() => onEvidence(x.id, x.label)}><Ic.Clip />Anexar</button>
                      )}
                    </td>
                    <td className={`c${st}`} data-label="Força real (IA)">{x.ia ? <Pill value={x.ia.sum} tier={x.ia.tier} /> : <span className="d-dash">—</span>}</td>
                    <td className={`c${st}`} data-label="Status">{x.ia ? <span className={`tbadge t-${x.ia.tier}`}>{TIER_WORD[x.ia.tier]}</span> : null}</td>
                    <td className={`dd-read${st}`}>{x.ia?.leitura || (updating ? 'Lendo…' : '—')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── 3 · a distribuição e o que ela diz ── */}
      <div className="gd-row gd-three">
        <Card icon={<Ic.Bars />} title="Distribuição da força real" sub="Força real por diferencial, de 3 a 15">
          <div className={`dd-dist${st}`}>
            <div className="dd-cols" role="list">
              {m.rows.map((x, i) => (
                <div key={x.id} className="dd-col" role="listitem" aria-label={`${x.label}: força real ${x.ia?.sum ?? '—'} de 15, sua avaliação ${x.user.sum}`}>
                  <div className="dd-track">
                    <span className="dd-user" style={{ '--h': `${(x.user.sum / 15) * 100}%` } as CSSProperties} title={`Sua avaliação: ${x.user.sum}`} />
                    <span className={`dd-b t-${x.ia?.tier ?? 'none'}`} style={{ '--h': `${((x.ia?.sum ?? 0) / 15) * 100}%` } as CSSProperties}><b>{x.ia?.sum ?? '—'}</b></span>
                  </div>
                  <span className="lb" title={x.label}>{x.label || `Diferencial ${i + 1}`}</span>
                </div>
              ))}
            </div>
            <p className="dd-leg"><i className="bar" />Força real (IA)<i className="tick" />Sua avaliação</p>
            <div className="dd-counts">
              <span><b>{m.n}</b>{m.n === 1 ? 'avaliado' : 'avaliados'}</span>
              <span className="t-forte"><b>{counts.forte}</b>{counts.forte === 1 ? 'forte' : 'fortes'}</span>
              <span className="t-moderado"><b>{counts.moderado}</b>{counts.moderado === 1 ? 'moderado' : 'moderados'}</span>
              <span className="t-fraco"><b>{counts.fraco}</b>{counts.fraco === 1 ? 'fraco' : 'fracos'}</span>
              <span><b>{avg == null ? '—' : n1(avg)}</b>média</span>
            </div>
          </div>
        </Card>
        <List icon={<Ic.Star />} title="Pontos fortes" kind="forte" items={reading?.fortes} updating={updating} />
        <List icon={<Ic.Warning />} title="Fragilidades e riscos" kind="risco" items={reading?.riscos} updating={updating} />
      </div>

      {/* ── 4 · o que fazer com isso ── */}
      <Card icon={<Ic.Target />} title="Recomendações estratégicas" sub="Em ordem de prioridade" className="dd-list dd-rec">
        {reading?.recomendacoes.length
          ? <ol className={`gd-recs dd-recs${st}`}>{reading.recomendacoes.map((x, i) => <li key={i}><span className="mono">{i + 1}</span>{x}</li>)}</ol>
          : reading ? <p className="gd-empty">Nada a recomendar por enquanto.</p> : <Waiting updating={updating} />}
      </Card>
    </div>
  );
}

function Waiting({ updating }: { updating: boolean }) {
  return updating ? <div className="obj-skel"><i /><i /><i /></div> : <p className="gd-empty">A IA calcula quando os diferenciais tiverem nome e as três notas.</p>;
}

function List({ icon, title, kind, items, updating }: { icon: ReactNode; title: string; kind: 'forte' | 'risco'; items?: string[]; updating: boolean }) {
  return (
    <Card icon={icon} title={title} sub={kind === 'forte' ? 'O que as evidências confirmam' : 'O que ainda não se sustenta'} className={`dd-list dd-${kind}`}>
      {items?.length
        ? <ul className={`dd-points${updating ? ' is-stale' : ''}`}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
        : items ? <p className="gd-empty">Nada a apontar por enquanto.</p> : <Waiting updating={updating} />}
    </Card>
  );
}
